import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_CONTEXT = { church_id: null, role: null, is_admin: false, members: [], admin_count: 0 };

/**
 * useSharedItems — church-level shared goals/priorities/tasks (Mongo-backed).
 * One source of truth per item; everyone reads/writes the same record.
 */
export function useSharedItems(userId, { focusRefetch = false } = {}) {
  const [sharedItems, setSharedItems] = useState([]);
  const [sharedContext, setSharedContext] = useState(EMPTY_CONTEXT);
  const [sharedLoaded, setSharedLoaded] = useState(false);
  const lastFetchRef = useRef(0);
  // Ids with a toggle/delete request in the air. A double-click on a shared
  // task otherwise fires complete then un-complete ~100ms apart, which for a
  // recurring task spawns the next occurrence and immediately deletes it.
  const inFlight = useRef(new Set());

  const refetchShared = useCallback(async () => {
    if (!userId) return;
    lastFetchRef.current = Date.now();
    try {
      const res = await fetch(`${API_URL}/api/planner/shared/items?user_id=${userId}`);
      if (res.ok) {
        const d = await res.json();
        setSharedItems(d.items || []);
      }
    } catch { /* network hiccup — keep current state */ }
    setSharedLoaded(true);
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    refetchShared();
    fetch(`${API_URL}/api/planner/shared/context/${userId}`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d) setSharedContext(d); })
      .catch(() => {});
  }, [userId, refetchShared]);

  // Instant cross-user sync: the backend pings 'shared_item_sync' over the
  // app WebSocket whenever another party creates/edits/toggles/deletes a
  // shared item — refetch immediately (tiny debounce to coalesce bursts).
  useEffect(() => {
    if (!userId) return;
    let t = null;
    const onSync = () => {
      clearTimeout(t);
      t = setTimeout(() => refetchShared(), 250);
    };
    window.addEventListener('ws_shared_item_sync', onSync);
    return () => {
      clearTimeout(t);
      window.removeEventListener('ws_shared_item_sync', onSync);
    };
  }, [userId, refetchShared]);

  // Cross-admin sync without new WebSockets: refetch when the tab regains
  // focus/visibility (throttled to once per 30s).
  useEffect(() => {
    if (!focusRefetch || !userId) return;
    const onFocus = () => {
      if (document.visibilityState === 'hidden') return;
      if (Date.now() - lastFetchRef.current > 30000) refetchShared();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [focusRefetch, userId, refetchShared]);

  const createSharedItem = useCallback(async (payload) => {
    const res = await fetch(`${API_URL}/api/planner/shared/items`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId, ...payload }),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to create shared item');
    }
    const item = await res.json();
    setSharedItems(prev => [...prev, item]);
    return item;
  }, [userId]);

  const toggleSharedItem = useCallback(async (item, completed) => {
    if (inFlight.current.has(item.id)) return;
    inFlight.current.add(item.id);
    // Optimistic flip with revert
    setSharedItems(prev => prev.map(i =>
      i.id === item.id
        ? { ...i, completed, completed_by_name: completed ? 'you' : null, completed_at: completed ? new Date().toISOString() : null }
        : i
    ));
    try {
      const res = await fetch(`${API_URL}/api/planner/shared/items/${item.id}/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, completed }),
      });
      if (res.status === 404) {
        setSharedItems(prev => prev.filter(i => i.id !== item.id));
        toast.error('This shared item was removed.');
        return;
      }
      if (!res.ok) throw new Error();
      // Recurring shared tasks roll forward: completing one spawns the next
      // occurrence (next_occurrence), un-completing deletes the spawned one
      // (removed_occurrence_id). The server also pings shared_item_sync, but
      // apply the response here so the list is right before that lands.
      const { next_occurrence, removed_occurrence_id, ...updated } = await res.json();
      setSharedItems(prev => {
        let list = prev.map(i => (i.id === item.id ? updated : i));
        if (removed_occurrence_id) list = list.filter(i => i.id !== removed_occurrence_id);
        if (next_occurrence && !list.some(i => i.id === next_occurrence.id)) list = [...list, next_occurrence];
        return list;
      });
    } catch {
      setSharedItems(prev => prev.map(i => (i.id === item.id ? item : i)));
      toast.error("Couldn't update the shared item. Please try again.");
    } finally {
      inFlight.current.delete(item.id);
    }
  }, [userId]);

  const updateSharedItem = useCallback(async (itemId, fields) => {
    const res = await fetch(`${API_URL}/api/planner/shared/items/${itemId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId, ...fields }),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to update shared item');
    }
    const updated = await res.json();
    setSharedItems(prev => prev.map(i => (i.id === itemId ? updated : i)));
    return updated;
  }, [userId]);

  const deleteSharedItem = useCallback(async (item) => {
    // Same in-flight guard as the toggle: a second click while the DELETE is
    // in the air would re-add the optimistically removed row on revert.
    if (inFlight.current.has(item.id)) return false;
    inFlight.current.add(item.id);
    setSharedItems(prev => prev.filter(i => i.id !== item.id));
    try {
      const res = await fetch(`${API_URL}/api/planner/shared/items/${item.id}?user_id=${userId}`, { method: 'DELETE' });
      if (!res.ok && res.status !== 404) throw new Error();
      return true;
    } catch {
      setSharedItems(prev => [...prev, item]);
      toast.error("Couldn't delete the shared item. Please try again.");
      return false; // callers that chain a second write (assign-undo) must stop here
    } finally {
      inFlight.current.delete(item.id);
    }
  }, [userId]);

  return {
    sharedItems,
    sharedContext,
    sharedLoaded,
    refetchShared,
    createSharedItem,
    toggleSharedItem,
    updateSharedItem,
    deleteSharedItem,
  };
}

/** Show incomplete tasks always; completed ones linger for 24h then self-clean. */
export function visibleSharedTasks(items) {
  const cutoff = Date.now() - 24 * 60 * 60 * 1000;
  return (items || [])
    .filter(i => i.item_type === 'task')
    .filter(i => !i.completed || (i.completed_at && new Date(i.completed_at).getTime() > cutoff))
    .sort((a, b) => {
      if (!!a.completed !== !!b.completed) return a.completed ? 1 : -1;
      const da = a.due_date || '9999-99-99';
      const db = b.due_date || '9999-99-99';
      if (da !== db) return da < db ? -1 : 1;
      return (a.created_at || '') < (b.created_at || '') ? -1 : 1;
    });
}
